import {
    CALIBRATION_EVENT_TYPE,
    CALIBRATION_UNIT_PART_TYPES,
    PART_REQUIRED_EVENT_TYPES,
    SINGLE_UNIT_COUNT,
    SOLE_UNIT_INDEX
} from '../../constants';
import { useEffect } from 'react';
import { toServiceDateInstant } from 'utils/dateTimeUtils/toServiceDateInstant';

import { useForm, useFormWatch } from '@american-autonomy/ag-ui';

import { ALL_UNITS_VALUE } from 'components/fields/UnitSelectPanelField/constants';

import useAssetParts from 'hooks/useAssetParts';

import type {
    AssetServiceLogCreateRequestCalibrationKind,
    LogServiceFormSchema,
    Props,
    UseData
} from './types';

const useData = ({ assetId, isSaving, onSubmit }: Props): UseData => {
    const { fetchAssetParts, parts } = useAssetParts();

    const form = useForm<LogServiceFormSchema>({
        defaultValues: { performedOn: new Date() },
        mode: 'onChange'
    });

    const { control, getValues, setValue } = form;

    const calibrationKind = useFormWatch({ control, name: 'calibrationKind' });
    const eventType = useFormWatch({ control, name: 'eventType' });
    const part = useFormWatch({ control, name: 'part' });
    const performedOn = useFormWatch({ control, name: 'performedOn' });
    const unit = useFormWatch({ control, name: 'unit' });

    useEffect(() => {
        fetchAssetParts({ assetId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetId]);

    const isCalibration = eventType === CALIBRATION_EVENT_TYPE;

    const isEventTypeChosen = !!eventType;

    const isPartVisible = isEventTypeChosen && !isCalibration;

    const isPartRequired = PART_REQUIRED_EVENT_TYPES.includes(eventType);

    const selectedPartType = part?.value ?? '';

    const selectedCalibrationKind = calibrationKind?.value as
        | AssetServiceLogCreateRequestCalibrationKind
        | undefined;

    const unitPartType = isCalibration
        ? ((selectedCalibrationKind && CALIBRATION_UNIT_PART_TYPES[selectedCalibrationKind]) ?? '')
        : selectedPartType;

    const targetPart = parts.find(({ partType }) => partType === unitPartType);

    const isTargetResolved = !unitPartType || !!targetPart;

    const isUnitVisible = !!targetPart && targetPart.unitCount > SINGLE_UNIT_COUNT;

    useEffect(() => {
        setValue('unit', undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitPartType]);

    useEffect(() => {
        if (isCalibration) {
            setValue('part', undefined);

            return;
        }

        setValue('calibrationKind', undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCalibration]);

    const isSaveDisabled =
        !!isSaving ||
        !isEventTypeChosen ||
        !isTargetResolved ||
        !performedOn ||
        (isCalibration && !selectedCalibrationKind) ||
        (isPartRequired && !selectedPartType) ||
        (isUnitVisible && !unit);

    const getUnitIndex = () => {
        if (!unitPartType) {
            return undefined;
        }

        if (!isUnitVisible) {
            return SOLE_UNIT_INDEX;
        }

        return unit?.value === ALL_UNITS_VALUE ? undefined : Number(unit?.value);
    };

    const handleSubmit = () => {
        const { notes, performedOn: performedOnValue } = getValues();

        onSubmit({
            calibrationKind: isCalibration ? selectedCalibrationKind : undefined,
            eventType,
            notes,
            partType: isCalibration ? undefined : selectedPartType || undefined,
            performedAt: toServiceDateInstant(performedOnValue),
            unitIndex: getUnitIndex()
        });
    };

    return {
        form,
        handleSubmit,
        isCalibration,
        isPartRequired,
        isPartVisible,
        isSaveDisabled,
        isUnitVisible,
        unitPartType
    };
};

export default useData;
